import { ReactElement, useCallback, useMemo } from 'react';
import { formatTranslation, TranslationKey, unsafeTranslationKey } from 'intl';
import { CommonTranslationKeys, FieldTranslationKeys } from 'intl/keys';

import { Spinner } from 'components/core';
import ViewWrapper from 'components/core/Layout/ViewWrapper';

import { useFieldSettingsStyles } from './styles';
import { useStyles } from 'components/common/forms/formStyles/styles';

import Divider from '@mui/material/Divider';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';

import { useIntl } from 'react-intl';

import { MemoButtonContained } from 'components/general/ButtonContained/ButtonContained';
import { FieldValuesSwitch } from '../switch';

import cn from 'classnames';
import { FieldBoxProps, FieldSettingsPresentationalProps } from './types';
import HustroFieldIcon from 'assets/icons/hustro_field_icon.svg?react';
import { MemoForbiddenTooltip } from 'components/general/ForbiddenTooltip/ForbiddenTooltip';
import { FieldBox } from 'components/fieldBox';
import { getFieldValueIdentifier } from 'presentation/fieldValue/fieldValueOnView';
import { findById } from 'shared/utils/id/findById';

export function FieldSettingsPresentational({
  sorted,
  loading,
  onFieldBoxClick,
  isPosting,
  fieldChanges,
  onCancel,
  onSubmit,
  hasChanges,
  titleLabelId,
  addNewOption,
}: FieldSettingsPresentationalProps): ReactElement {
  const intl = useIntl();
  const classes = useStyles();
  const specificClasses = useFieldSettingsStyles();

  const submitDisabledReason = useMemo((): TranslationKey | undefined => {
    if (isPosting) {
      return CommonTranslationKeys.disabledSaveInProgress;
    }
    if (!hasChanges) {
      return CommonTranslationKeys.disabledNothingToSave;
    }
    return undefined;
  }, [isPosting, hasChanges]);

  return (
    <>
      <ViewWrapper>
        <div className={specificClasses.root}>
          <div className={specificClasses.titleContainer}>
            <div className={specificClasses.titleTextContainer}>
              <h2 className={specificClasses.title}>
                {formatTranslation(intl, unsafeTranslationKey(titleLabelId) )}
              </h2>
            </div>
          </div>
          <div className={specificClasses.dividerContainer}>
            <Divider />
          </div>

          <div className={specificClasses.addButtonContainer}>
            <MemoButtonContained onClick={addNewOption}>
              {formatTranslation(intl, FieldTranslationKeys.valuesAddOption)}
            </MemoButtonContained>
          </div>

          <div className={specificClasses.fieldBoxes}>
            {loading ? (
              <Spinner
                position='static'
                reason='FieldSettings Single Presentational loading'
              />
            ) : (
              Object.values(sorted).map((fieldValue) => (
                <FieldSettingBox
                  key={getFieldValueIdentifier(fieldValue)}
                  disabled={isPosting}
                  onFieldBoxClick={onFieldBoxClick}
                  fieldValue={fieldValue}
                  fieldChanges={fieldChanges}
                  icon={
                    fieldValue.canEditTranslations ? undefined : (
                      <HustroFieldIcon />
                    )
                  }
                />
              ))
            )}
          </div>
          <div className={specificClasses.bottomSpace} />
        </div>
      </ViewWrapper>
      <div className={cn(classes.submitFooter, classes.castShadow)}>
        <Divider className={cn(classes.fullWidth)} />
        <div className={classes.footerPadding}>
          <Button onClick={onCancel} disabled={Boolean(isPosting)}>
            {formatTranslation(intl, CommonTranslationKeys.cancel)}
          </Button>
          <div>
            <MemoForbiddenTooltip
              visibleWhen={Boolean(isPosting) || !hasChanges}
              reason={submitDisabledReason}
            >
              <MemoButtonContained
                onClick={onSubmit}
                endIcon={
                  isPosting ? <CircularProgress size={16} /> : undefined
                }
                disabled={isPosting || !hasChanges}
              >
                {formatTranslation(intl, CommonTranslationKeys.save)}
              </MemoButtonContained>
            </MemoForbiddenTooltip>
          </div>
        </div>
      </div>
    </>
  );
}

function FieldSettingBox({
  fieldValue,
  onFieldBoxClick,
  disabled,
  fieldChanges,
  icon,
}: FieldBoxProps): ReactElement {
  const specificClasses = useFieldSettingsStyles();
  const { locale } = useIntl();

  const changes = useMemo(
    () =>
      fieldValue._id ? findById(fieldChanges, fieldValue._id) : undefined,
    [fieldChanges, fieldValue._id]
  );

  const onClick = useCallback(() => {
    if (disabled) return;
    onFieldBoxClick({
      ...fieldValue,
      ...changes,
    });
  }, [disabled, changes, onFieldBoxClick, fieldValue]);

  const label = useMemo(() => {
    if (changes && Array.isArray(changes.valueTranslations)) {
      const currentLanguageTranslation =
        changes.valueTranslations.find((translation) => {
          return translation.localeCode.includes(locale);
        }) || changes.valueTranslations[0];
      return currentLanguageTranslation.value;
    }
    return fieldValue.label;
  }, [changes, fieldValue, locale]);

  return (
    <FieldBox>
      <div className={specificClasses.flexRowCenter} onClick={onClick}>
        {icon}
        <span className={specificClasses.fieldLabel}>{label}</span>
      </div>
      <FieldValuesSwitch
        checked={Boolean(fieldValue.processes.length)}
        _id={getFieldValueIdentifier(fieldValue)}
        disabled={disabled}
      />
    </FieldBox>
  );
}
